import { getToken } from 'firebase/messaging';
import { getRollbar } from 'shared/services/error-tracking';
import { ID } from 'shared/types/id';
import { isApnsSupported } from 'shared/utils/is-apns-supported';

import { convertApnsToken } from './convert-apns-token';
import { deletePushToken } from './delete-push-token';
import {
  getPushNotificationToken,
  getRawStoredToken,
  initPushNotifications,
} from './helpers';

const getElectronToken = async (): Promise<string | undefined> => {
  let result =
    (await window.electron?.getFcmToken?.()) ??
    (await window.electron?.getPushToken?.());

  if (typeof result === 'string') {
    result = { token: result };
  }

  // macOS convert APNs token
  if (isApnsSupported()) {
    const apnsToken = result?.token;
    if (apnsToken) {
      return await convertApnsToken(apnsToken);
    }
    return undefined;
  }
  // old electron version and windows support
  return result?.token;
};

export const getPushToken = async (uid: ID) => {
  if (!uid) {
    return;
  }

  try {
    const serviceWorkerRegistration =
      await window.navigator.serviceWorker?.ready;

    const messaging = initPushNotifications();

    const cachedToken = getPushNotificationToken(uid);
    if (cachedToken) {
      return cachedToken;
    }

    // Clean up stale token from Firestore before fetching a new one
    const staleToken = getRawStoredToken(uid);
    if (staleToken) {
      deletePushToken(staleToken).catch(() => {});
    }

    return (
      (await getElectronToken()) ??
      (await getToken(messaging, {
        serviceWorkerRegistration,
        vapidKey: import.meta.env.VITE_FIREBASE_VAPID_KEY,
      }))
    );
  } catch (error) {
    getRollbar().error(
      'failed to get firebase push-notification token',
      error as Error,
    );
  }
};
